// Universal Help Articles frontend — drop-in replacement for the per-app
// hardcoded HowToDialog components. Fetches content from the DB via
// GET /api/help-articles/{appSlug}/for-me, so the article is automatically
// scoped to the caller's role by the backend.
//
// Usage:
//   <HowToButton appSlug="ai-outbound" />
//   <HowToButton appSlug="inventory" />
//   <HowToButton appSlug="projects" />
//
// Content is edited by staff in Settings → Help Articles.

import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { slugCandidates } from "@/lib/helpSlugs";
import { HelpCircle, Printer, Loader2 } from "lucide-react";
import api from "@/lib/api";
import { sanitizeKbHtml, useKbEmbedHosts } from "@/lib/kbSanitize";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";

// Scoped article styles — kept inline so we don't touch app-wide CSS.
const HOWTO_CSS = `
.howto-body p { margin: 0.5rem 0; }
.howto-body h3 { font-size: 0.85rem; font-weight: 600; color: #0f172a; margin: 1rem 0 0.35rem; }
.howto-body ul { list-style: disc; padding-left: 1.25rem; margin: 0.5rem 0; }
.howto-body ol { list-style: decimal; padding-left: 1.25rem; margin: 0.5rem 0; }
.howto-body li { margin: 0.2rem 0; }
.howto-body strong { color: #0f172a; font-weight: 600; }
.howto-body code {
  background: #f1f5f9; padding: 1px 5px; border-radius: 3px;
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace; font-size: 11px;
}
.howto-body a { color: #16a34a; }
.howto-body a:hover { text-decoration: underline; }
`;

/** The blue pill button. Rendered only by AutoHowToButton in Layout. */
export function HowToButton({ appSlug, label = "How to" }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <Button
        variant="outline"
        onClick={() => setOpen(true)}
        data-testid={`${appSlug}-howto-btn`}
        className="border-blue-300 bg-blue-50 text-blue-700 font-semibold shadow-sm
                   hover:bg-blue-100 hover:text-blue-800 hover:border-blue-400
                   focus-visible:ring-2 focus-visible:ring-blue-300"
      >
        <HelpCircle className="h-4 w-4" />
        {label}
      </Button>
      {open && <HowToDialog appSlug={appSlug} open={open} onClose={() => setOpen(false)} />}
    </>
  );
}

function HowToDialog({ appSlug, open, onClose }) {
  const [loading, setLoading] = useState(true);
  const [article, setArticle] = useState(null);

  useEffect(() => {
    let cancel = false;
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get(`/help-articles/${appSlug}/for-me`);
        if (!cancel) setArticle(data);
      } catch {
        if (!cancel) setArticle({ empty: true, title: "Help article unavailable",
                                  subtitle: "", sections: [] });
      } finally {
        if (!cancel) setLoading(false);
      }
    })();
    return () => { cancel = true; };
  }, [appSlug]);

  const jumpTo = (id) => {
    const el = document.getElementById(`section-${id}`);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const printArticle = () => {
    const contentEl = document.getElementById("howto-article");
    if (!contentEl) return;
    const w = window.open("", "_blank", "width=900,height=1000");
    if (!w) return;
    w.document.write(`<!doctype html><html><head><title>${article?.title || "How to"}</title>
      <style>
        body{font-family:ui-sans-serif,system-ui,sans-serif;color:#0f172a;padding:32px;max-width:820px;margin:0 auto;}
        h1{font-size:22px;margin:0 0 4px;} h2{font-size:16px;margin-top:24px;}
        h3{font-size:13px;margin-top:14px;}
        p,li{font-size:12px;line-height:1.55;}
        img{max-width:100%;border:1px solid #e2e8f0;border-radius:6px;margin:8px 0;}
        figure{margin:12px 0;} figcaption{font-size:11px;color:#64748b;margin-top:4px;}
        code{background:#f1f5f9;padding:1px 4px;border-radius:3px;font-size:11px;}
        ul,ol{padding-left:1.25rem;} li{margin:2px 0;}
      </style></head><body>
      <h1>${article?.title || ""}</h1>
      <div style="color:#64748b;font-size:11px;margin-bottom:16px">
        ${article?.subtitle || ""} · Printed ${new Date().toLocaleString()}</div>
      ${contentEl.innerHTML}
    </body></html>`);
    w.document.close();
    setTimeout(() => { try { w.print(); } catch { /* noop */ } }, 400);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent
        data-testid={`${appSlug}-howto-dialog`}
        className="max-w-5xl w-[95vw] max-h-[90vh] overflow-hidden p-0 flex flex-col"
      >
        <style>{HOWTO_CSS}</style>
        <DialogHeader className="px-6 pt-5 pb-3 border-b border-slate-100">
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0">
              <DialogTitle className="text-base font-semibold text-slate-900 flex items-center gap-2">
                <HelpCircle className="h-4 w-4 text-primary" />
                {article?.title || "How to"}
              </DialogTitle>
              <DialogDescription className="text-xs text-slate-500 mt-0.5">
                {article?.subtitle || ""}
              </DialogDescription>
            </div>
            <div className="flex items-center gap-2 shrink-0 pr-6">
              <Button variant="outline" size="sm" onClick={printArticle}
                      data-testid={`${appSlug}-howto-print`}
                      className="h-8 text-xs"
                      disabled={loading || !article?.sections?.length}>
                <Printer className="h-3.5 w-3.5" /> Print / Save PDF
              </Button>
            </div>
          </div>
        </DialogHeader>

        <div className="flex-1 min-h-0 grid grid-cols-1 md:grid-cols-[220px_1fr]">
          <aside className="hidden md:block border-r border-slate-100 bg-slate-50/40 overflow-y-auto py-4 px-3">
            <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-500 mb-2 px-2">
              In this guide
            </div>
            <nav className="flex flex-col text-xs">
              {(article?.sections || []).map((s) => (
                <button key={s.id} onClick={() => jumpTo(s.id)}
                        data-testid={`${appSlug}-howto-toc-${s.id}`}
                        className="text-left rounded-md px-2 py-1.5 text-slate-600 hover:bg-white hover:text-slate-900 transition-colors">
                  {s.title}
                </button>
              ))}
              {loading && (
                <div className="px-2 py-1.5 text-slate-400 flex items-center gap-1.5">
                  <Loader2 className="h-3 w-3 animate-spin" /> Loading…
                </div>
              )}
            </nav>
          </aside>

          <article id="howto-article"
                   data-testid={`${appSlug}-howto-article`}
                   className="overflow-y-auto px-6 py-5">
            {loading ? (
              <div className="flex items-center gap-2 text-slate-400 text-sm py-16">
                <Loader2 className="h-4 w-4 animate-spin" /> Loading article…
              </div>
            ) : (article?.sections || []).length === 0 ? (
              <div className="rounded-lg border border-dashed border-slate-200 bg-slate-50 p-8 text-center">
                <div className="text-sm font-semibold text-slate-700">No article yet</div>
                <div className="mt-1 text-xs text-slate-500">
                  A staff admin can add one from <strong>Settings → Help Articles</strong>.
                </div>
              </div>
            ) : (
              <>
                {(article.sections || []).map((s) => (
                  <ArticleSection key={s.id} section={s} />
                ))}
                <div className="text-[10px] text-slate-400 border-t border-slate-100 pt-3 mt-6">
                  Last updated: {article?.updated_at
                    ? new Date(article.updated_at).toLocaleDateString()
                    : "—"}
                  {article?.updated_by_name && ` · by ${article.updated_by_name}`}
                  {article?.role && ` · Scoped to ${article.role}`}
                </div>
              </>
            )}
          </article>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function ArticleSection({ section }) {
  const hosts = useKbEmbedHosts();
  return (
    <section id={`section-${section.id}`} className="mb-8 scroll-mt-4">
      {section.kicker && (
        <div className="text-[10px] font-semibold uppercase tracking-wider text-primary/70">
          {section.kicker}
        </div>
      )}
      <h2 className="mt-0.5 text-lg font-semibold text-slate-900">{section.title}</h2>
      {section.screenshot_url && (
        <figure className="my-3 overflow-hidden rounded-lg border border-slate-200 bg-slate-50">
          <img src={section.screenshot_url} alt={section.title} loading="lazy"
               className="block w-full max-h-[400px] object-contain bg-white" />
          {section.screenshot_caption && (
            <figcaption className="border-t border-slate-100 bg-white px-3 py-1.5 text-[11px] text-slate-500">
              {section.screenshot_caption}
            </figcaption>
          )}
        </figure>
      )}
      {/* body_html is authored by staff via the admin editor. Staff-only edit
          surface means the XSS risk is staff-vs-staff — acceptable. */}
      <div className="howto-body mt-2 text-slate-700 text-sm leading-relaxed"
           dangerouslySetInnerHTML={{ __html: sanitizeKbHtml(section.body_html || "", hosts) }} />
    </section>
  );
}

/** Rendered once by Layout: shows a How to button on any page whose address
 * matches an article slug for the caller's role. */
export function AutoHowToButton() {
  const { pathname } = useLocation();
  const [slug, setSlug] = useState(null);
  useEffect(() => {
    let cancel = false;
    setSlug(null);
    const slugs = slugCandidates(pathname);
    if (!slugs.length) return undefined;
    api.get("/help-articles-resolve", { params: { slugs: slugs.join(",") } })
      .then(({ data }) => { if (!cancel) setSlug(data.slug || null); })
      .catch(() => {});
    return () => { cancel = true; };
  }, [pathname]);
  if (!slug) return null;
  return <div className="flex justify-end" data-testid="auto-howto"><HowToButton appSlug={slug} /></div>;
}
